/**
 * M27 task-07 — LLM model registry list (ADM-01/02; HUB-082). Family-A refine
 * `useTable` → `<DataTable>` (db-connectors/List precedent) over
 * GET /llm-models. Columns are module-level (no callbacks) so the no-jsdom
 * seam test can render them through a raw `useReactTable` harness. Delete lives
 * on the Edit page (a bound model returns 409, surfaced there).
 *
 * The API key is WRITE-ONLY: the response carries only `hasApiKey`, so the list
 * shows a presence indicator, never key material.
 */
import { useTable } from '@refinedev/react-table';
import { type ColumnDef } from '@tanstack/react-table';
import { Link } from 'react-router';
import type { LlmModel } from '@verybigthings/trackframe-shared/schemas';
import { buttonVariants } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/DataTable';
import { TableSearch } from '@/components/TableSearch';
import { useSearchFilter } from '@/lib/use-search-filter';
import { cn } from '@/lib/utils';

export const modelColumns: ColumnDef<LlmModel>[] = [
  {
    id: 'internalName',
    header: 'Name',
    accessorKey: 'internalName',
    cell: (c) => (
      <Link
        to={`/admin/models/edit/${c.row.original.id}`}
        className="font-medium underline-offset-2 hover:underline"
      >
        {c.row.original.internalName}
      </Link>
    ),
  },
  {
    id: 'provider',
    header: 'Provider',
    accessorKey: 'provider',
    cell: (c) => <code className="font-mono text-xs">{c.getValue() as string}</code>,
  },
  {
    id: 'modelName',
    header: 'Model',
    accessorKey: 'modelName',
    cell: (c) => <code className="font-mono text-xs">{c.getValue() as string}</code>,
  },
  {
    id: 'hasApiKey',
    header: 'API key',
    accessorKey: 'hasApiKey',
    cell: (c) =>
      c.getValue() ? (
        <span className="text-xs font-medium text-emerald-600">Key set ✓</span>
      ) : (
        <span className="text-xs text-muted-foreground">No key set</span>
      ),
  },
  {
    id: 'baseUrl',
    header: 'Base URL',
    accessorKey: 'baseUrl',
    cell: (c) => {
      const v = c.getValue() as string | null;
      return v ? <code className="font-mono text-xs">{v}</code> : <span className="text-muted-foreground">—</span>;
    },
  },
  {
    id: 'isDefault',
    header: 'Default',
    accessorKey: 'isDefault',
    cell: (c) =>
      c.getValue() ? (
        <Badge variant="success" className="rounded-full">
          default
        </Badge>
      ) : (
        <span className="text-muted-foreground">—</span>
      ),
  },
  {
    id: 'actions',
    header: '',
    cell: (c) => (
      <div className="flex justify-end">
        <Link
          to={`/admin/models/edit/${c.row.original.id}`}
          className={cn(buttonVariants({ size: 'sm', variant: 'ghost' }))}
        >
          Edit
        </Link>
      </div>
    ),
  },
];

export function ModelsList() {
  const {
    reactTable: table,
    refineCore: { setFilters, filters },
  } = useTable<LlmModel>({ columns: modelColumns });
  const [search, setSearch] = useSearchFilter(setFilters, filters);

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Models</h1>
          <p className="text-sm text-muted-foreground">
            Named LLM models the hub can use. Each purpose is bound to one of these under Model Assignment.
          </p>
        </div>
        <Link to="/admin/models/create" className={cn(buttonVariants())}>
          New model
        </Link>
      </div>
      <DataTable
        table={table}
        toolbar={<TableSearch value={search} onChange={setSearch} placeholder="Filter by name, provider, or model…" />}
      />
    </div>
  );
}
